import React, { useState, useEffect } from 'react';
import {
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Stepper,
  Step,
  StepLabel,
  Box,
  Autocomplete,
  Alert,
  AlertTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ExpandMore,
  LocationOn,
  Category,
  Description,
  AttachFile,
  Send,
  Preview,
  CheckCircle,
  Error as ErrorIcon,
  Info,
  Warning,
  Close,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import api from '../../utils/api';
import { getUserId } from '../../utils/api';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import LogOut from '../Account/LogOut';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import '../EnhancedHeader.css';

// Enhanced New Ticket Dialog Styles
const dialogStyles = {
  stepCard: {
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
    }
  },
  friendlyInput: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      transition: 'all 0.2s ease',
      '&:hover': {
        boxShadow: '0 2px 8px rgba(102, 126, 234, 0.15)',
      },
      '&.Mui-focused': {
        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.25)',
      }
    }
  }
};

const EnhancedNewTicketDialog = ({ open, onClose, onTicketCreated }) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [activeStep, setActiveStep] = useState(0);
  const [category1, setCategory1] = useState([]);
  const [category2, setCategory2] = useState([]);
  const [category3, setCategory3] = useState([]);
  const [shellOptions, setShellOptions] = useState([]);
  const [businessProcessOptions, setBusinessProcessOptions] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [showUnifierFields, setShowUnifierFields] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [attachmentError, setAttachmentError] = useState('');
  const MySwal = withReactContent(Swal);

  const MAX_ATTACHMENTS = 5;
  const MAX_ATTACHMENT_SIZE_BYTES = 5 * 1024 * 1024;

  const steps = [
    { label: 'Location & Department', icon: '📍', description: 'Tell us where you are' },
    { label: 'Issue Details', icon: '📝', description: 'Describe what you need help with' },
    { label: 'Additional Information', icon: '📎', description: 'Add any extra details' },
    { label: 'Review & Submit', icon: '✅', description: 'Double-check and send' }
  ];

  const validationSchema = Yup.object().shape({
    location: Yup.string().required('Please select your current location'),
    ServiceCategory1: Yup.string().required('Select Department to provide you service'),
    ServiceCategory2: Yup.string().required('Select the general issue'),
    ServiceCategory3: Yup.string().required('Select the specific issue'),
    Issue: Yup.string().required('Issue description is required').min(10, 'Please provide more details (minimum 10 characters)'),
    attachments: Yup.array()
      .max(MAX_ATTACHMENTS, `Maximum ${MAX_ATTACHMENTS} attachments allowed`)
      .of(
        Yup.object().shape({
          filesize: Yup.number().max(MAX_ATTACHMENT_SIZE_BYTES, 'Each file must be 5MB or smaller'),
        })
      ),
    new_shell: Yup.string().when('ServiceCategory1', {
      is: (val) => val && category1.find(cat => cat.id === val)?.name.toLowerCase().includes('unifier'),
      then: (schema) => schema.required('Shell field is required'),
    }),
    new_businessprocess: Yup.string().when('ServiceCategory1', {
      is: (val) => val && category1.find(cat => cat.id === val)?.name.toLowerCase().includes('unifier'),
      then: (schema) => schema.required('Business Process field is required'),
    }),
    new_project: Yup.string().when('ServiceCategory1', {
      is: (val) => val && category1.find(cat => cat.id === val)?.name.toLowerCase().includes('unifier'),
      then: (schema) => schema.required('Project field is required'),
    }),
    new_recordno: Yup.string().when('ServiceCategory1', {
      is: (val) => val && category1.find(cat => cat.id === val)?.name.toLowerCase().includes('unifier'),
      then: (schema) => schema.required('Record Number field is required'),
    }),
  });

  useEffect(() => {
    // Helper function to retry failed requests
    const fetchWithRetry = async (url, retries = 3, delay = 1000) => {
      for (let i = 0; i < retries; i++) {
        try {
          const response = await axios.get(url, { timeout: 15000 });
          return response;
        } catch (error) {
          if (i === retries - 1) throw error;
          console.warn(`Retry ${i + 1}/${retries} for ${url}`);
          await new Promise(resolve => setTimeout(resolve, delay * (i + 1)));
        }
      }
    };

    const fetchData = async () => {
      // Fetch categories independently
      try {
        const cat1Response = await fetchWithRetry(`${process.env.REACT_APP_Back_URL}/tickets/category1`);
        setCategory1(cat1Response.data.map(row => ({ id: row.new_category1id, name: row.new_name })));
      } catch (error) {
        console.error('Error fetching category1:', error);
        MySwal.fire({
          icon: 'warning',
          title: 'Unable to Load Departments',
          text: 'Could not load department categories. Please refresh the page.',
          confirmButtonColor: '#B41736',
        });
      }

      try {
        const cat2Response = await fetchWithRetry(`${process.env.REACT_APP_Back_URL}/tickets/category2`);
        setCategory2(cat2Response.data.map(row => ({ 
          id: row.new_category2id, 
          name: row.new_name, 
          parent_category: row._new_parentcategory_value 
        })));
      } catch (error) {
        console.error('Error fetching category2:', error);
        MySwal.fire({
          icon: 'warning',
          title: 'Unable to Load Issue Categories',
          text: 'Could not load general issue categories. Please refresh the page.',
          confirmButtonColor: '#B41736',
        });
      }

      try {
        const cat3Response = await fetchWithRetry(`${process.env.REACT_APP_Back_URL}/tickets/category3`);
        setCategory3(cat3Response.data.map(row => ({ 
          id: row.new_category3id, 
          name: row.new_name, 
          parent_category: row._new_parentcategory_value 
        })));
      } catch (error) {
        console.error('Error fetching category3:', error);
        MySwal.fire({
          icon: 'warning',
          title: 'Unable to Load Specific Issues',
          text: 'Could not load specific issue categories. Please refresh the page.',
          confirmButtonColor: '#B41736',
        });
      }

      // Fetch Unifier options
      try {
        const mvcasesResponse = await fetchWithRetry('https://ticketapi.rowad-app.com/tickets/mvcases');
        const data = mvcasesResponse.data;
        
        setShellOptions(data.filter(item => item.new_type === 'shell').map(item => ({ id: item.new_mvcaseid, name: item.new_name })));
        setBusinessProcessOptions(data.filter(item => item.new_type === 'business').map(item => ({ id: item.new_mvcaseid, name: item.new_name })));
        setProjectOptions(data.filter(item => item.new_type === 'project').map(item => ({ id: item.new_mvcaseid, name: item.new_name })));
      } catch (error) {
        console.error('Error fetching Unifier options:', error);
        // Unifier options are optional, so just log the error
      }
    };

    fetchData();
  }, []);

  const handleNext = async (validateForm, values) => {
    const errors = await validateForm();
    const currentStepFields = getStepFields(activeStep);
    const stepErrors = Object.keys(errors).filter(key => currentStepFields.includes(key));
    
    if (stepErrors.length === 0) {
      setActiveStep(prev => prev + 1);
    }
  };
  
  const handleBack = () => setActiveStep(prev => prev - 1);

  const getStepFields = (step) => {
    switch (step) {
      case 0:
        return ['location', 'ServiceCategory1'];
      case 1:
        return ['ServiceCategory2', 'ServiceCategory3', 'Issue'];
      case 2:
        return ['new_shell', 'new_businessprocess', 'new_project', 'new_recordno'];
      case 3:
        return ['token'];
      default:
        return [];
    }
  };

  const handlePreview = (values) => {
    setPreviewData(values);
    setShowPreview(true);
  };

  const renderStepContent = (step, values, errors, touched, setFieldValue) => {
    switch (step) {
      case 0:
        return (
          <Box>
            {/* Step Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h4" sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#51647C' }}>
                📍 Where Are You Located?
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Help us connect you with the right support team by telling us your location and department
              </Typography>
            </Box>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card sx={{ 
                  p: 3, 
                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(255, 255, 255, 0.9) 100%)',
                  border: '1px solid',
                  borderColor: 'primary.light',
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 25px rgba(102, 126, 234, 0.15)',
                    transform: 'translateY(-2px)'
                  }
                }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControl fullWidth error={errors.location && touched.location} sx={dialogStyles.friendlyInput}>
                        <InputLabel>🌍 Current Location</InputLabel>
                        <Select
                          value={values.location}
                          onChange={(e) => {
                            setFieldValue('location', e.target.value);
                            setFieldValue('ServiceCategory1', '');
                            setFieldValue('ServiceCategory2', '');
                            setFieldValue('ServiceCategory3', '');
                          }}
                          label="🌍 Current Location"
                        >
                          <MenuItem value="head_office">Head Office المكتب الرئيسي</MenuItem>
                          <MenuItem value="site">Site الموقع</MenuItem>
                        </Select>
                        {errors.location && touched.location && (
                          <Typography color="error" variant="caption">{errors.location}</Typography>
                        )}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                          💼 Which department can help you today?
                        </Typography>
                      </Box>
                      <FormControl fullWidth disabled={!values.location} error={errors.ServiceCategory1 && touched.ServiceCategory1} sx={dialogStyles.friendlyInput}>
                        <InputLabel>Select Department</InputLabel>
                        <Select
                          value={values.ServiceCategory1}
                          onChange={(e) => {
                            setFieldValue('ServiceCategory1', e.target.value);
                            setFieldValue('ServiceCategory2', '');
                            setFieldValue('ServiceCategory3', '');
                            const selectedCategory = category1.find(cat => cat.id === e.target.value);
                            setShowUnifierFields(selectedCategory?.name.toLowerCase().includes('unifier'));
                          }}
                          label="Department"
                        >
                          {category1
                            .filter(category => {
                              if (values.location === 'head_office') {
                                return !category.name.toLowerCase().includes('site');
                              } else if (values.location === 'site') {
                                return !category.name.toLowerCase().includes('head office');
                              }
                              return true;
                            })
                            .map((category) => (
                              <MenuItem key={category.id} value={category.id}>
                                {category.name}
                              </MenuItem>
                            ))}
                        </Select>
                        {errors.ServiceCategory1 && touched.ServiceCategory1 && (
                          <Typography color="error" variant="caption">{errors.ServiceCategory1}</Typography>
                        )}
                      </FormControl>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            </Grid>
          </Box>
        );

      case 1:
        return (
          <Box>
            {/* Step Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h4" sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#51647C' }}>
                📝 Tell Us About Your Issue
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Help us understand what you need so we can provide the best possible support
              </Typography>
            </Box>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card sx={{ 
                  p: 3, 
                  background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.05) 0%, rgba(255, 255, 255, 0.9) 100%)',
                  border: '1px solid',
                  borderColor: 'success.light',
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 25px rgba(76, 175, 80, 0.15)',
                    transform: 'translateY(-2px)'
                  }
                }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth disabled={!values.ServiceCategory1} error={errors.ServiceCategory2 && touched.ServiceCategory2}>
                        <InputLabel>General Issue</InputLabel>
                        <Select
                          value={values.ServiceCategory2}
                          onChange={(e) => {
                            setFieldValue('ServiceCategory2', e.target.value);
                            setFieldValue('ServiceCategory3', '');
                          }}
                          label="General Issue"
                        >
                          {category2
                            .filter(item => item.parent_category === values.ServiceCategory1)
                            .map((category) => (
                              <MenuItem key={category.id} value={category.id}>
                                {category.name}
                              </MenuItem>
                            ))}
                        </Select>
                        {errors.ServiceCategory2 && touched.ServiceCategory2 && (
                          <Typography color="error" variant="caption">{errors.ServiceCategory2}</Typography>
                        )}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth disabled={!values.ServiceCategory2} error={errors.ServiceCategory3 && touched.ServiceCategory3}>
                        <InputLabel>Specific Issue</InputLabel>
                        <Select
                          value={values.ServiceCategory3}
                          onChange={(e) => setFieldValue('ServiceCategory3', e.target.value)}
                          label="Specific Issue"
                        >
                          {category3
                            .filter(item => item.parent_category === values.ServiceCategory2)
                            .map((category) => (
                              <MenuItem key={category.id} value={category.id}>
                                {category.name}
                              </MenuItem>
                            ))}
                        </Select>
                        {errors.ServiceCategory3 && touched.ServiceCategory3 && (
                          <Typography color="error" variant="caption">{errors.ServiceCategory3}</Typography>
                        )}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Issue Description"
                        placeholder="Please describe your issue in detail..."
                        value={values.Issue}
                        onChange={(e) => setFieldValue('Issue', e.target.value)}
                        error={errors.Issue && touched.Issue}
                        helperText={errors.Issue && touched.Issue ? errors.Issue : 'The more details you provide, the faster we can help you'}
                      />
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            </Grid>
          </Box>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            {showUnifierFields && (
              <Grid item xs={12}>
                <Card>
                  <CardHeader 
                    title="Unifier Information"
                    subheader="Additional details required for Unifier-related requests"
                  />
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Autocomplete
                          multiple
                          options={shellOptions}
                          getOptionLabel={(option) => option.name}
                          onChange={(_, values) => {
                            const combinedValue = values.map(v => v.name).join(', ');
                            setFieldValue('new_shell', combinedValue);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Shell(s)"
                              error={errors.new_shell && touched.new_shell}
                              helperText={errors.new_shell && touched.new_shell ? errors.new_shell : undefined}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Autocomplete
                          multiple
                          options={businessProcessOptions}
                          getOptionLabel={(option) => option.name}
                          onChange={(_, values) => {
                            const combinedValue = values.map(v => v.name).join(', ');
                            setFieldValue('new_businessprocess', combinedValue);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Business Process(es)"
                              error={errors.new_businessprocess && touched.new_businessprocess}
                              helperText={errors.new_businessprocess && touched.new_businessprocess ? errors.new_businessprocess : undefined}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Autocomplete
                          multiple
                          options={projectOptions}
                          getOptionLabel={(option) => option.name}
                          onChange={(_, values) => {
                            const combinedValue = values.map(v => v.name).join(', ');
                            setFieldValue('new_project', combinedValue);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Project(s)"
                              error={errors.new_project && touched.new_project}
                              helperText={errors.new_project && touched.new_project ? errors.new_project : undefined}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Record Number"
                          value={values.new_recordno}
                          onChange={(e) => setFieldValue('new_recordno', e.target.value)}
                          error={errors.new_recordno && touched.new_recordno}
                          helperText={errors.new_recordno && touched.new_recordno ? errors.new_recordno : undefined}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}

            <Grid item xs={12}>
              <Card>
                <CardHeader 
                  avatar={<AttachFile />} 
                  title="Attachments (Optional)"
                  subheader="Upload relevant files to help us understand your issue"
                />
                <CardContent>
                  {attachmentError && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAttachmentError('')}>
                      {attachmentError}
                    </Alert>
                  )}
                  <input
                    type="file"
                    multiple
                    onChange={(e) => {
                      setAttachmentError('');

                      const selectedFiles = e.target.files ? Array.from(e.target.files) : [];
                      if (selectedFiles.length === 0) return;

                      const existing = Array.isArray(values.attachments) ? values.attachments : [];
                      if (existing.length + selectedFiles.length > MAX_ATTACHMENTS) {
                        setAttachmentError(`You can upload up to ${MAX_ATTACHMENTS} attachments.`);
                        e.target.value = '';
                        return;
                      }

                      const tooLarge = selectedFiles.find(f => f.size > MAX_ATTACHMENT_SIZE_BYTES);
                      if (tooLarge) {
                        setAttachmentError(`"${tooLarge.name}" exceeds 5MB. Please select files up to 5MB each.`);
                        e.target.value = '';
                        return;
                      }

                      Promise.all(
                        selectedFiles.map(
                          (file) =>
                            new Promise((resolve, reject) => {
                              const reader = new FileReader();
                              reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
                              reader.onload = () => {
                                const result = typeof reader.result === 'string' ? reader.result : '';
                                resolve({
                                  filename: file.name,
                                  isdocument: true,
                                  mimetype: file.type,
                                  documentbody: result.split('base64,')[1],
                                  filesize: file.size,
                                });
                              };
                              reader.readAsDataURL(file);
                            })
                        )
                      )
                        .then((newAttachments) => {
                          setFieldValue('attachments', [...existing, ...newAttachments]);
                          e.target.value = '';
                        })
                        .catch((err) => {
                          console.error(err);
                          setAttachmentError('Failed to read one or more files. Please try again.');
                          e.target.value = '';
                        });
                    }}
                  />
                  {Array.isArray(values.attachments) && values.attachments.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Selected files
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {values.attachments.map((file, index) => (
                          <Box
                            key={`${file.filename}-${index}`}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              p: 1.5,
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 2,
                              bgcolor: 'rgba(0,0,0,0.02)',
                            }}
                          >
                            <Typography variant="body2" sx={{ mr: 2 }}>
                              📎 {file.filename} ({(file.filesize / 1024 / 1024).toFixed(2)} MB)
                            </Typography>
                            <Button
                              type="button"
                              variant="outlined"
                              color="error"
                              size="small"
                              onClick={() => {
                                const updated = values.attachments.filter((_, i) => i !== index);
                                setFieldValue('attachments', updated);
                              }}
                            >
                              Remove
                            </Button>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Maximum: {MAX_ATTACHMENTS} files, 5MB each
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );

      case 3:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="info">
                <AlertTitle>Review Your Ticket</AlertTitle>
                Please review your ticket details before submitting. You can go back to make changes if needed.
              </Alert>
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { 
          minHeight: '85vh',
          maxHeight: '95vh',
          borderRadius: 3,
          background: 'linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%)',
        }
      }}
    >
      <DialogTitle sx={{ 
        background: '#51647C',
        color: 'white',
        p: 3,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              width: '80%',
              pr: 2 
            }}>
              <Box sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)', 
                borderRadius: '50%', 
                p: 1.5, 
                mr: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                🎫
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: '#ffffff' }}>
                  Create New Ticket
                </Typography>
                {/* <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  We're here to help! Let's get your issue resolved quickly.
                </Typography> */}
              </Box>
            </Box>
            <Box sx={{ width: '4%', display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton 
                onClick={onClose} 
                sx={{ 
                  color: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
                }}
              >
                <Close />
              </IconButton>
            </Box>
          </Box>
        </Box>
        
        {/* Decorative elements */}
        <Box sx={{
          position: 'absolute',
          top: -20,
          right: -20,
          width: 100,
          height: 100,
          borderRadius: '50%',
          bgcolor: 'rgba(255,255,255,0.1)',
          opacity: 0.3
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: -30,
          left: -30,
          width: 80,
          height: 80,
          borderRadius: '50%',
          bgcolor: 'rgba(255,255,255,0.1)',
          opacity: 0.2
        }} />
      </DialogTitle>

      <DialogContent sx={{ p: 4, bgcolor: 'transparent' }}>
          
          {/* Enhanced Progress Indicator */}
          <Box sx={{ mb: 4, p: 3, bgcolor: 'rgba(255,255,255,0.8)', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                📋 Step {activeStep + 1} of {steps.length}
              </Typography>
              <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
                • {steps[activeStep].description}
              </Typography>
            </Box>
            
            <Stepper 
              activeStep={activeStep} 
              alternativeLabel
              sx={{ 
                '& .MuiStepLabel-root': { cursor: 'pointer' },
                '& .MuiStepIcon-root': { 
                  fontSize: '2rem',
                  '&.Mui-active': { color: 'primary.main' },
                  '&.Mui-completed': { color: 'success.main' }
                }
              }}
            >
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel
                    StepIconComponent={() => (
                      <Box sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: index <= activeStep ? (index === activeStep ? 'primary.main' : 'success.main') : 'grey.300',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.2rem',
                        fontWeight: 'bold',
                        transition: 'all 0.3s ease'
                      }}>
                        {index < activeStep ? '✓' : step.icon}
                      </Box>
                    )}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 600, mt: 1 }}>
                      {step.label}
                    </Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          <Formik
            initialValues={{
              location: "",
              ServiceCategory1: "",
              ServiceCategory2: "",
              ServiceCategory3: "",
              Issue: "",
              attachments: [],
              new_shell: "",
              new_businessprocess: "",
              new_project: "",
              new_recordno: ""
            }}
            validationSchema={validationSchema}
            onSubmit={async (values, { resetForm, setSubmitting }) => {
              try {
                setSubmitting(true);
                let recaptchaToken = '';
                if (executeRecaptcha) {
                  recaptchaToken = await executeRecaptcha('create_ticket');
                }
                const { location, ...caseValues } = values;
                const caseData = {
                  case: {
                    "token": recaptchaToken,
                    "new_Category1@odata.bind": `/new_category1s(${caseValues.ServiceCategory1})`,
                    "new_Category2@odata.bind": `/new_category2s(${caseValues.ServiceCategory2})`,
                    "new_Category3@odata.bind": `/new_category3s(${caseValues.ServiceCategory3})`,
                    "new_Requester@odata.bind": `/new_userstses(${getUserId()})`,
                    "new_description": caseValues.Issue,
                    ...(caseValues.new_shell && { new_shell: caseValues.new_shell }),
                    ...(caseValues.new_businessprocess && { new_businessprocess: caseValues.new_businessprocess }),
                    ...(caseValues.new_project && { new_project: caseValues.new_project }),
                    ...(caseValues.new_recordno && { new_recordno: caseValues.new_recordno }),
                  },
                  attachments: Array.isArray(caseValues.attachments) && caseValues.attachments.length > 0 ? caseValues.attachments : null,
                };

                await axios.post(
                  `${process.env.REACT_APP_Back_URL}/tickets/cases`, 
                  caseData,
                  { timeout: 15000 }
                );
                
                MySwal.fire({
                  title: "Ticket Submitted Successfully!",
                  text: "Your ticket has been created",
                  icon: "success",
                  timer: 2000,
                  timerProgressBar: true,
                }).then(() => {
                  onTicketCreated && onTicketCreated();
                  onClose();
                  setActiveStep(0);
                });
                
                resetForm();
              } catch (error) {
                console.error("Error submitting form:", error);
                
                let errorMessage = "Failed to submit ticket. Please try again.";
                if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
                  errorMessage = "Request timed out. The server took too long to respond. Please check your connection and try again.";
                }
                
                MySwal.fire({
                  title: "Error",
                  text: errorMessage,
                  icon: "error",
                  confirmButtonColor: "#B41736",
                });
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ values, errors, touched, setFieldValue, isSubmitting, handleSubmit, validateForm, setTouched }) => (
              <Form>
                {renderStepContent(activeStep, values, errors, touched, setFieldValue)}
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                  <Button
                    onClick={handleBack}
                    disabled={activeStep === 0}
                    variant="outlined"
                    sx={{ width: '10%' }}
                  >
                    Back
                  </Button>
                  
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {activeStep === steps.length - 1 && (
                      <Button
                        onClick={() => handlePreview(values)}
                        variant="outlined"
                        startIcon={<Preview />}
                      >
                        Preview
                      </Button>
                    )}
                    
                    {activeStep === steps.length - 1 ? (
                      <LoadingButton
                        variant="contained"
                        loading={isSubmitting}
                        type="submit"
                        startIcon={<Send />}
                        sx={{
                          background: "#B41736",
                          "&:hover": {
                            background: "#8f1129",
                          },
                        }}
                      >
                        Submit Ticket
                      </LoadingButton>
                    ) : (
                      <Button
                        onClick={async () => {
                          const currentStepFields = getStepFields(activeStep);
                          const touchedFields = {};
                          currentStepFields.forEach(field => touchedFields[field] = true);
                          await setTouched(touchedFields);
                          await handleNext(validateForm, values);
                        }}
                        variant="contained"
                        sx={{
                          background: "#B41736",
                          "&:hover": {
                            background: "#8f1129",
                          },
                        }}
                      >
                        Next
                      </Button>
                    )}
                  </Box>
                </Box>
              </Form>
            )}
          </Formik>

          {/* Preview Dialog */}
          <Dialog open={showPreview} onClose={() => setShowPreview(false)} maxWidth="md" fullWidth>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                Ticket Preview
                <IconButton onClick={() => setShowPreview(false)}>
                  <Close />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              {previewData && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6">Location & Department</Typography>
                    <Typography>Location: {previewData.location}</Typography>
                    <Typography>Department: {category1.find(c => c.id === previewData.ServiceCategory1)?.name}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="h6">Issue Details</Typography>
                    <Typography>Category: {category2.find(c => c.id === previewData.ServiceCategory2)?.name}</Typography>
                    <Typography>Specific Issue: {category3.find(c => c.id === previewData.ServiceCategory3)?.name}</Typography>
                    <Typography>Description: {previewData.Issue}</Typography>
                  </Grid>
                </Grid>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowPreview(false)}>Close</Button>
            </DialogActions>
          </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default EnhancedNewTicketDialog;
